<script setup lang="ts">
import { Question, Select, Input } from "../../fields";
import { usePensionOptionsStore, PaymentArrears } from "@/stores/pensionOptions";
import { pensionOptionsValidator } from "@/utils/validators";
import useVuelidate from "@vuelidate/core";
import { scrollErrorIntoView } from "@/composables/scrollErrorIntoView";
import { isDecimal } from "@/utils";
import { ref } from "vue";

const store = usePensionOptionsStore();
const v$ = useVuelidate();

const props = defineProps<{
    quoteNumber: string,
    isAmsHandoffQuote: boolean,
    hasAtLeastOneExpiredPremium: boolean,
    loading: boolean
}>();
const emit = defineEmits(["requote", "amsGetGuaranteedQuotes"]);

const adjustQuoteToggle = ref(false);

const requote = async () => {
    const isValid = await v$.value.$validate();
    if (isValid) {
        emit("requote");
    } else {
        scrollErrorIntoView();
    }
};

const amsGetGuaranteedQuotes = async () => {
    const isValid = await v$.value.$validate();
    if (isValid) {
        emit("amsGetGuaranteedQuotes");
    } else {
        scrollErrorIntoView();
    }
};

const validator = pensionOptionsValidator(store);
</script>

<template>
    <div class="col-12 d-lg-none">
        <button
            id="mobile-toggle-adjust-quote"
            class="mb-3 btn btn-tertiary col-12"
            type="button"
            @click="adjustQuoteToggle = !adjustQuoteToggle"
        >
            <span aria-hidden="true">Adjust my quote</span>
        </button>
    </div>
    <div
        id="adj"
        class="results-sum-box col-12 col-md-12 col-lg-3 pt-0 pb-0 collapse d-lg-block hidden-sm"
        :class="{ 'show': adjustQuoteToggle }"
    >
        <div>
            <div class="row p-0 sessioncamhidetext">
                <div class="h4 blue mb-0 col-12 py-2">
                    Adjust your quote
                </div>
                <label class="col-12 col-form-label blue">Your quote number:</label>
                <div class="blue my-auto col-12 pb-2">
                    {{ props.quoteNumber }}
                </div>
            </div>
            <Question
                name="paymentFrequency"
                label="Payment frequency"
                :include-padding="true"
                input-class="col-12"
            >
                <Select
                    v-model="store.paymentFrequency"
                    col="col-12"
                    placeholder=" Please Select"
                    :options="[
                        {label: 'Monthly',value: 'monthly'},
                        {label: 'Quarterly',value: 'quarterly'},
                        {label: 'Half yearly',value: 'halfYearly'},
                        {label: 'Yearly',value: 'annually'}
                    ]"
                    :validation="validator.paymentFrequency"
                />
            </Question>
            <Question
                name="paymentArrears"
                label="Payment type"
                input-class="col-12"
            >
                <Select
                    v-model="store.paymentArrears"
                    col="col-12"
                    placeholder=" Please Select"
                    :options="[
                        {label: 'In arrears',value: PaymentArrears.InArrears},
                        {label: 'In advance',value: PaymentArrears.InAdvance},
                    ]"
                    :validation="validator.paymentArrears"
                />
            </Question>
            <Question
                name="totalFund"
                label="Total value of pension fund(s) (including any tax-free lump sum you need)"
                input-class="col-12"
            >
                <Input
                    v-model.number="store.totalFund"
                    type="currency"
                    inputmode="text"
                    class="col-12"
                    placeholder="0"
                    :validation="validator.totalFund"
                    :filter="isDecimal"
                />
            </Question>
            <p v-if="props.hasAtLeastOneExpiredPremium">
                To get new quotes for any that have expired, you will need to click the 'Update results' button. This will update all quotes. Remember, rates can go up or down.
            </p>
            <div class="row form-group">
                <div class="col-12 mt-2">
                    <button
                        v-if="isAmsHandoffQuote"
                        id="results_update_results_button"
                        type="button"
                        class="btn btn-primary"
                        @click="amsGetGuaranteedQuotes"
                    >
                        Get guaranteed quotes
                    </button>
                    <div v-else>
                        <button
                            id="results_update_results_button"
                            type="button"
                            class="btn btn-primary"
                            :disabled="props.loading"
                            @click="requote"
                        >
                            <span
                                v-if="props.loading"
                                class="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                            {{ props.loading ? 'Loading...' : 'Update results' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.max-fund {
    margin-top: 10px;
}
</style>
